
// src/components/comunicacion/ForosTematicos.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../../../../api";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId, isSessionSuperAdmin, getSessionEmpresaId } from "../../../../utils/SessionUser";
import { toISODateTime } from "../../../../utils/DateHelpers";
import { useLogos } from "../../../../context/LogoProvider";
import sigmaDelete from '../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../Pages/img/Editar.png';
import ConfirmDeleteDialog from "../../../security/ConfirmDeleteDialog";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";

const SERVER_URL = API_BASE.replace(/\/api\/?$/, '');
const UPLOAD_ENDPOINT = `${API_BASE}/upload/foro`;

const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http") || path.startsWith("data:")) return path;
    const baseUrl = API_BASE.replace(/\/api\/?$/, "");
    return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

const ESTADOS = [
    { value: "todos", label: "Todos" },
    { value: "abierto", label: "Abiertos" },
    { value: "cerrado", label: "Cerrados" },
];

const ForosTematicos = ({ onNavigate, initialItem }) => {
    const { getSystemConfig } = useLogos();
    // Estados principales
    const [foros, setForos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [areasNegocio, setAreasNegocio] = useState([]);
    const [estadosIntranet, setEstadosIntranet] = useState([]);

    const [filtros, setFiltros] = useState({
        texto: "",
        area: "Todas",
        categoria: "Todas",
        estado: "todos",
    });

    const [nuevoForo, setNuevoForo] = useState({
        titulo: "",
        descripcionInicial: "",
        area: null,
        categoria: null,
        estado: null,
        destacado: false,
        imagen: null,
    });

    const [erroresForo, setErroresForo] = useState({});
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [editingId, setEditingId] = useState(null); // ID si se está editando, null si creando
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null }); // Nuevo estado
    const [alertDialog, setAlertDialog] = useState({ open: false, title: "", message: "", variant: "info" });
    const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
    const [showModal, setShowModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [descendantCompanies, setDescendantCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState({});
    const [sharingLoading, setSharingLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [previousImagePath, setPreviousImagePath] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [modalTab, setModalTab] = useState('info'); // 'info' | 'comments'

    const [nuevoComentario, setNuevoComentario] = useState("");
    const [erroresComentario, setErroresComentario] = useState("");
    const [nombreUsuarioActual, setNombreUsuarioActual] = useState("Colaborador");
    const [fotoUsuarioActual, setFotoUsuarioActual] = useState(null);
    const [usuariosMap, setUsuariosMap] = useState({});
    const [fotosMap, setFotosMap] = useState({});

    const dataLoaded = React.useRef(false);

    // Obtener usuarios y personas para mapeo de nombres
    useEffect(() => {
        if (dataLoaded.current) return;
        dataLoaded.current = true;

        const cargarUsuariosYPersonas = async () => {
            try {
                // Fetch users and persons in parallel
                const [resUsuarios, resPersonas] = await Promise.all([
                    axios.get(ENDPOINTS.usuarios),
                    axios.get(ENDPOINTS.personas)
                ]);

                // Create a map of PersonId -> FullName and Photo
                const personasMap = {};
                const personasFotoMap = {};
                resPersonas.data.forEach(p => {
                    const idP = String(p.idPersona || p.IdPersona || 0);
                    personasMap[idP] = `${p.nombre || p.Nombre || ''} ${p.apellidoPaterno || p.ApellidoPaterno || ''} ${p.apellidoMaterno || p.ApellidoMaterno || ''}`.trim();
                    personasFotoMap[idP] = p.fotoPerfil || p.FotoPerfil || null;
                });

                // Create a map of UserId -> FullName (using the PersonId link)
                const map = {};
                const fMap = {};
                resUsuarios.data.forEach(u => {
                    const idPers = String(u.idPersona || u.IdPersona || 0);
                    const idUsr = String(u.idUsuario || u.IdUsuario || 0);
                    if (idPers !== "0") {
                        if (personasMap[idPers]) map[idUsr] = personasMap[idPers];
                        if (personasFotoMap[idPers]) fMap[idUsr] = personasFotoMap[idPers];
                    }
                });
                setUsuariosMap(map);
                setFotosMap(fMap);

                // Set current session user name
                const idCurrent = String(getSessionUserId());
                if (map[idCurrent]) {
                    setNombreUsuarioActual(map[idCurrent]);
                }
                if (fMap[idCurrent]) {
                    setFotoUsuarioActual(fMap[idCurrent]);
                }
            } catch (error) {
                console.error("Error al cargar usuarios y personas:", error);
            }
        };
        cargarUsuariosYPersonas();
    }, []);

    // Cargar datos desde API
    useEffect(() => {
        const cargarDatos = async () => {
            try {
                // Cargar categorías
                const resCategorias = await axios.get(ENDPOINTS.categorias);
                const cats = resCategorias.data;
                setCategorias(cats);

                // Cargar áreas de negocio (con filtro manual para consistencia con catálogo)
                const resAreas = await axios.get(ENDPOINTS.areasNegocios);
                const rawAreas = resAreas.data || [];
                const isSuper = isSessionSuperAdmin();
                const myEmpresaId = getSessionEmpresaId();

                const filteredAreas = rawAreas.filter(a => {
                    if (isSuper) return true;
                    const empId = a.idEmpresa || a.IdEmpresa;
                    return empId === myEmpresaId || !empId || empId === 0;
                });

                setAreasNegocio(filteredAreas);

                // Cargar estados y filtrar solo Abierto/Cerrado
                const resEstados = await axios.get(ENDPOINTS.estadosIntranet);
                const estados = resEstados.data.filter(e => {
                    const nombre = (e.nombre || e.Nombre || "").toLowerCase();
                    return nombre === "abierto" || nombre === "cerrado";
                });
                setEstadosIntranet(estados);

                // Crear mapeos de ID a nombre
                const areasMap = {};
                filteredAreas.forEach(a => {
                    areasMap[a.idAreaNegocio || a.IdAreaNegocio] = a.nombre || a.Nombre;
                });

                const categoriasMap = {};
                cats.forEach(c => {
                    categoriasMap[c.idCategoria || c.IdCategoria] = c.nombre || c.Nombre;
                });

                const estadosMap = {};
                estados.forEach(e => {
                    estadosMap[e.idEstado || e.IdEstado] = e.nombre || e.Nombre;
                });

                // Cargar foros
                const resForos = await axios.get(ENDPOINTS.foros);

                // Mapear foros con nombres en lugar de IDs
                const forosMapeados = resForos.data.map(f => ({
                    id: (f.idForo ?? f.IdForo),
                    titulo: (f.titulo ?? f.Titulo),
                    descripcionInicial: (f.descripcionInicial ?? f.DescripcionInicial),
                    area: areasMap[f.idAreaNegocio ?? f.IdAreaNegocio] || "Sin área",
                    idAreaNegocio: (f.idAreaNegocio ?? f.IdAreaNegocio),
                    categoria: categoriasMap[f.idCategoria ?? f.IdCategoria] || "Sin categoría",
                    idCategoria: (f.idCategoria ?? f.IdCategoria),
                    estado: estadosMap[f.idEstado ?? f.IdEstado]?.toLowerCase() || "abierto",
                    idEstado: (f.idEstado ?? f.IdEstado),
                    destacado: (f.destacado ?? f.Destacado),
                    imagen: (f.imagenUrl ?? f.ImagenUrl),
                    creadoPor: (f.idUsuarioCreador ?? f.IdUsuarioCreador) || "Sistema",
                    fechaCreacionRaw: (f.fechaCreacion ?? f.FechaCreacion),
                    fechaCreacion: (f.fechaCreacion ?? f.FechaCreacion) ? new Date(f.fechaCreacion ?? f.FechaCreacion).toLocaleString() : "",
                    esOrigen: (f.esOrigen ?? f.EsOrigen) || false,
                    comentarios: []
                }));

                // Ordenar: Destacados primero, luego por fecha de creación descendente
                forosMapeados.sort((a, b) => {
                    if (a.destacado && !b.destacado) return -1;
                    if (!a.destacado && b.destacado) return 1;
                    const dateA = new Date(a.fechaCreacion || 0).getTime();
                    const dateB = new Date(b.fechaCreacion || 0).getTime();
                    if (dateB !== dateA) return dateB - dateA;
                    return b.id - a.id; // Clave inmutable para orden absoluto
                });

                setForos(forosMapeados);

                if (forosMapeados.length > 0 && !initialItem) {
                    setSelectedId(forosMapeados[0].id);
                }

                // Setear valores por defecto en el formulario
                if (cats.length > 0 || filteredAreas.length > 0 || estados.length > 0) {
                    const defaultCat = cats[0]?.idCategoria || cats[0]?.IdCategoria || null;
                    const defaultArea = filteredAreas[0]?.idAreaNegocio || filteredAreas[0]?.IdAreaNegocio || null;
                    const defaultEstado = estados.find(e => (e.nombre || e.Nombre)?.toLowerCase() === "abierto")?.idEstado
                        || estados.find(e => (e.nombre || e.Nombre)?.toLowerCase() === "abierto")?.IdEstado
                        || estados[0]?.idEstado || estados[0]?.IdEstado || null;
                    
                    // Nota: corregí un typo de IdAreaNegocio por IdEstado arriba, pero revisando el código original:
                    // el original tenía un error de lógica en el fallback de IdEstado usando IdAreaNegocio? No, parece que fue un typo mío al leer o escribir.
                    // Voy a ponerlo bien.

                    setNuevoForo(prev => ({
                        ...prev,
                        categoria: defaultCat,
                        area: defaultArea,
                        estado: defaultEstado
                    }));
                }
            } catch (error) {
                console.error("Error al cargar datos:", error);
            }
        };
        cargarDatos();
    }, [initialItem]);

    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleNuevoForoChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNuevoForo((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setErroresForo((prev) => ({ ...prev, imagen: 'Solo se permiten archivos de imagen.' }));
            return;
        }

        const maxMB = parseFloat(getSystemConfig('LIMITE_PESO_IMAGENES', '5'));
        if (file.size > maxMB * 1024 * 1024) {
            setErroresForo((prev) => ({ ...prev, imagen: `La imagen no debe superar los ${maxMB}MB.` }));
            return;
        }

        setSelectedFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
        setErroresForo((prev) => ({ ...prev, imagen: '' }));
    };

    const validarNuevoForo = () => {
        const nuevo = {};
        if (!nuevoForo.titulo || !nuevoForo.titulo.trim()) {
            nuevo.titulo = "El título es obligatorio.";
        }
        if (!nuevoForo.descripcionInicial || !nuevoForo.descripcionInicial.trim()) {
            nuevo.descripcionInicial = "La descripción inicial es obligatoria.";
        }
        // Validar imagen: ya no es obligatoria
        /* if (!selectedFile && !nuevoForo.imagen) {
            nuevo.imagen = "La imagen es obligatoria.";
        } */
        setErroresForo(nuevo);
        return Object.keys(nuevo).length === 0;
    };

    const recargarForos = async () => {
        try {
            const resForos = await axios.get(ENDPOINTS.foros);

            // Crear mapeos usando los estados actuales (soportando PascalCase)
            const areasMap = {};
            areasNegocio.forEach(a => {
                areasMap[a.idAreaNegocio || a.IdAreaNegocio] = a.nombre || a.Nombre;
            });
            const categoriasMap = {};
            categorias.forEach(c => {
                categoriasMap[c.idCategoria || c.IdCategoria] = c.nombre || c.Nombre;
            });
            const estadosMap = {};
            estadosIntranet.forEach(e => {
                estadosMap[e.idEstado || e.IdEstado] = e.nombre || e.Nombre;
            });

            // Mapear foros (soportando PascalCase)
            const forosMapeados = resForos.data.map(f => ({
                id: f.idForo || f.IdForo,
                titulo: f.titulo || f.Titulo,
                descripcionInicial: f.descripcionInicial || f.DescripcionInicial,
                area: areasMap[f.idAreaNegocio || f.IdAreaNegocio] || "Sin área",
                idAreaNegocio: f.idAreaNegocio || f.IdAreaNegocio,
                categoria: categoriasMap[f.idCategoria || f.IdCategoria] || "Sin categoría",
                idCategoria: f.idCategoria || f.IdCategoria,
                estado: (estadosMap[f.idEstado || f.IdEstado] || "").toLowerCase() || "abierto",
                idEstado: f.idEstado || f.IdEstado,
                destacado: f.destacado || f.Destacado,
                imagen: f.imagenUrl || f.ImagenUrl,
                creadoPor: f.idUsuarioCreador || f.IdUsuarioCreador || "Sistema",
                fechaCreacionRaw: f.fechaCreacion || f.FechaCreacion,
                fechaCreacion: (f.fechaCreacion || f.FechaCreacion) ? new Date(f.fechaCreacion || f.FechaCreacion).toLocaleString() : "",
                esOrigen: f.esOrigen || f.EsOrigen || false,
                comentarios: f.comentarios || f.Comentarios || []
            }));

            // Ordenar: Destacados primero, luego por fecha de creaci n descendente
            forosMapeados.sort((a, b) => {
                if (a.destacado && !b.destacado) return -1;
                if (!a.destacado && b.destacado) return 1;
                const dateA = new Date(a.fechaCreacion || 0).getTime();
                const dateB = new Date(b.fechaCreacion || 0).getTime();
                if (dateB !== dateA) return dateB - dateA;
                return b.id - a.id; // Clave inmutable para orden absoluto
            });

            setForos(forosMapeados);
        } catch (error) {
            console.error("Error al recargar foros:", error);
        }
    };

    const handleToggleDestacadoForo = async (foro) => {
        try {
            const idUsuario = getSessionUserId();
            const payload = {
                idForo: foro.id,
                titulo: foro.titulo,
                descripcionInicial: foro.descripcionInicial,
                idAreaNegocio: foro.idAreaNegocio,
                idCategoria: foro.idCategoria,
                idEstado: foro.idEstado || 1,
                destacado: !foro.destacado,
                imagenUrl: foro.imagen || "",
                idUsuarioCreador: foro.creadoPor ? Number(foro.creadoPor) : idUsuario,
                fechaCreacion: foro.fechaCreacionRaw || foro.fechaCreacion || toISODateTime(new Date()),
                fechaModificacion: toISODateTime(new Date()),
                activo: true
            };

            await axios.put(ENDPOINTS.foros, payload);

            // Actualizar estado local
            setForos(prev => {
                const updated = prev.map(f =>
                    f.id === foro.id ? { ...f, destacado: !f.destacado } : f
                );
                // Re-ordenar
                return updated.sort((a, b) => {
                    if (a.destacado && !b.destacado) return -1;
                    if (!a.destacado && b.destacado) return 1;
                    const dateA = new Date(a.fechaCreacion || 0).getTime();
                    const dateB = new Date(b.fechaCreacion || 0).getTime();
                    if (dateB !== dateA) return dateB - dateA;
                    return b.id - a.id;
                });
            });
        } catch (error) {
            console.error("Error al actualizar destacado del foro:", error);
            setAlertDialog({ open: true, title: "Error", message: "No se pudo actualizar el estado, inténtalo de nuevo.", variant: "error" });
        }
    };

    const executePublish = async (selectedShareCompanies = {}) => {
        try {
            const idUsuario = getSessionUserId();
            let finalImageUrl = nuevoForo.imagen;

            setIsUploading(true);
            setSharingLoading(true);

            // Subir imagen si existe
            if (selectedFile) {
                // Si estamos editando y hay una imagen previa, eliminarla de S3 primero
                if (editingId && previousImagePath) {
                    try {
                        await axios.delete(UPLOAD_ENDPOINT, {
                            params: { filePath: previousImagePath }
                        });
                    } catch (err) {
                        console.warn("No se pudo eliminar la imagen anterior de S3:", err);
                    }
                }

                const formDataImg = new FormData();
                formDataImg.append('file', selectedFile);
                const resImg = await axios.post(UPLOAD_ENDPOINT, formDataImg, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                finalImageUrl = resImg.data.filePath;
                setPreviousImagePath(finalImageUrl);
            }

            const payload = {
                idForo: editingId || 0,
                titulo: nuevoForo.titulo,
                descripcionInicial: nuevoForo.descripcionInicial,
                idAreaNegocio: parseInt(nuevoForo.area) || (areasNegocio.length > 0 ? areasNegocio[0].idAreaNegocio : 1),
                idCategoria: parseInt(nuevoForo.categoria) || (categorias.length > 0 ? categorias[0].idCategoria : 1),
                idEstado: parseInt(nuevoForo.estado) || (estadosIntranet.length > 0 ? estadosIntranet[0].idEstado : 1),
                destacado: nuevoForo.destacado,
                imagenUrl: finalImageUrl || "",
                idUsuarioCreador: idUsuario,
                idEmpresa: getSessionEmpresaId(),
                fechaCreacion: editingId ? (nuevoForo.fechaCreacion || toISODateTime(new Date())) : toISODateTime(new Date()),
                fechaModificacion: toISODateTime(new Date()),
                activo: true
            };

            let currentForoId = editingId;
            if (editingId) {
                payload.idForo = editingId;
                await axios.put(ENDPOINTS.foros, payload);
            } else {
                const resPost = await axios.post(ENDPOINTS.foros, payload);
                currentForoId = resPost.data;
            }

            if (currentForoId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.forosCompartidosByForo(currentForoId));
                    const existingShared = resShared.data || [];
                    
                    const selectedIds = Object.keys(selectedShareCompanies).map(Number);
                    
                    for (const idEmpStr of Object.keys(selectedShareCompanies)) {
                        const empId = Number(idEmpStr);
                        const isSelected = selectedShareCompanies[empId];
                        const existingRec = existingShared.find(s => s.idEmpresa === empId);

                        if (isSelected && !existingRec) {
                            await axios.post(ENDPOINTS.forosCompartidos, {
                                idForo: currentForoId,
                                idEmpresa: empId,
                                activo: true
                            });
                        } else if (!isSelected && existingRec) {
                            await axios.delete(`${ENDPOINTS.forosCompartidos}/${existingRec.idForoCompartido || existingRec.IdForoCompartido}`);
                        }
                    }
                } catch(shareErr) {
                    console.error("Error updating sharing permissions", shareErr);
                }
            }

            // Recargar foros desde API
            await recargarForos();
            setAlertDialog({ open: true, title: "Éxito", message: editingId ? "Foro actualizado correctamente." : "Foro creado correctamente.", variant: "success" });

            // Limpiar formulario
            setNuevoForo({
                titulo: "",
                descripcionInicial: "",
                area: areasNegocio[0]?.idAreaNegocio || null,
                categoria: categorias[0]?.idCategoria || null,
                estado: estadosIntranet.find(e => e.nombre?.toLowerCase() === "abierto")?.idEstado || estadosIntranet[0]?.idEstado || null,
                destacado: false,
                imagen: null,
            });
            setErroresForo({});
            setSelectedFile(null);
            setImagePreview(null);
            setPreviousImagePath(null);
            setEditingId(null);
            setShowModal(false);
            setShowShareModal(false);
        } catch (error) {
            console.error("Error al guardar foro:", error);
            setAlertDialog({ open: true, title: "Error", message: "Error al guardar el foro.", variant: "error" });
        } finally {
            setIsUploading(false);
            setSharingLoading(false);
        }
    };

    const handleCrearForo = async (e) => {
        if (e) e.preventDefault();
        if (!validarNuevoForo()) return;

        try {
            setIsUploading(true);
            const sessionEmpresaId = getSessionEmpresaId();
            
            // Obtener empresas descendientes
            const resDesc = await axios.get(ENDPOINTS.empresasDescendientes(sessionEmpresaId));
            const descendientes = resDesc.data || [];
            
            if (descendientes.length === 0) {
               await executePublish({});
               return;
            }

            let preSelected = {};
            if (editingId) {
               try {
                  const resShared = await axios.get(ENDPOINTS.forosCompartidosByForo(editingId));
                  const sharedData = resShared.data || [];
                  sharedData.forEach(sh => {
                      if (!sh.esOrigen && !sh.EsOrigen) {
                          const empId = sh.IdEmpresa || sh.idEmpresa;
                          preSelected[empId] = true;
                      }
                  });
               } catch(e) { console.warn("Could not fetch shared", e); }
            }

            setDescendantCompanies(descendientes);
            setSelectedCompanies(preSelected);
            setShowModal(false);
            setShowShareModal(true);
        } catch (error) {
            console.error('Error fetching descendants:', error);
            setAlertDialog({ open: true, title: "Error", message: "No se pudo verificar las empresas disponibles para compartir.", variant: "error" });
        } finally {
            setIsUploading(false);
        }
    };

    const handleNuevoTema = () => {
        const defaultCat = categorias[0]?.idCategoria || categorias[0]?.IdCategoria || null;
        const defaultArea = areasNegocio[0]?.idAreaNegocio || areasNegocio[0]?.IdAreaNegocio || null;
        const defaultEstado = estadosIntranet.find(e => (e.nombre || e.Nombre)?.toLowerCase() === "abierto")?.idEstado
            || estadosIntranet.find(e => (e.nombre || e.Nombre)?.toLowerCase() === "abierto")?.IdEstado
            || estadosIntranet[0]?.idEstado || estadosIntranet[0]?.IdEstado || null;

        setNuevoForo({
            titulo: "",
            descripcionInicial: "",
            area: defaultArea,
            categoria: defaultCat,
            estado: defaultEstado,
            destacado: false,
            imagen: null,
        });
        setErroresForo({});
        setSelectedFile(null);
        setPreviousImagePath(null);
        setImagePreview(null);
        setEditingId(null);
        setModalMode('create');
        setModalTab('info');
        setShowModal(true);
    };

    const handleSeleccionarForo = (foro) => {
        setSelectedId(foro.id);
        setErroresComentario("");

        // Cargar datos del foro en el formulario para editar
        setEditingId(foro.id);
        setNuevoForo({
            titulo: foro.titulo,
            descripcionInicial: foro.descripcionInicial,
            area: foro.idAreaNegocio,
            categoria: foro.idCategoria,
            estado: foro.idEstado,
            destacado: foro.destacado,
            imagen: foro.imagen || null,
            fechaCreacion: foro.fechaCreacionRaw || foro.fechaCreacion,
        });
        setImagePreview(foro.imagen ? (foro.imagen.startsWith('http') ? foro.imagen : `${SERVER_URL}${foro.imagen.startsWith('/') ? foro.imagen : '/' + foro.imagen}`) : null);
        setSelectedFile(null);
        setPreviousImagePath(foro.imagen || null);

        setModalMode('edit');
        setModalTab('info');
        setShowModal(true);
    };

    // Función reutilizable para cargar respuestas
    const cargarRespuestas = useCallback(async (idForo = selectedId) => {
        if (!idForo) return;

        try {
            const resRespuestas = await axios.get(`${ENDPOINTS.forosRespuestas}?idForo = ${idForo} `);

            // Filtrar client-side porque el backend retorna todo
            const filteredData = resRespuestas.data.filter(r => (r.idForo || r.IdForo) === idForo);

            const respuestasMapeadas = filteredData.map(r => {
                const nombreAutor = usuariosMap[r.idUsuario || r.IdUsuario] || r.autor || r.Autor || r.nombreUsuario || "Colaborador";
                return {
                    id: r.idRespuesta || r.IdRespuesta,
                    idUsuario: r.idUsuario || r.IdUsuario,
                    autor: nombreAutor,
                    mensaje: r.mensaje || r.Mensaje,
                    fecha: r.fechaCreacion || r.FechaCreacion
                };
            });

            // Ordenar por fecha (más antiguos primero, formato chat)
            respuestasMapeadas.sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());

            setForos(prev => prev.map(f =>
                f.id === idForo ? { ...f, comentarios: respuestasMapeadas } : f
            ));
        } catch (error) {
            console.error("Error al cargar respuestas:", error);
        }
    }, [selectedId, usuariosMap]);

    // Efecto para cargar respuestas cuando cambia el ID seleccionado o el mapa de usuarios
    useEffect(() => {
        if (showModal && modalMode === 'edit' && selectedId) {
            cargarRespuestas(selectedId);
        }
    }, [selectedId, showModal, modalMode, cargarRespuestas]);

    // Deep linking handler
    useEffect(() => {
        if (initialItem && foros.length > 0) {
            const found = foros.find(f => f.id === initialItem.id);
            if (found) {
                // If we navigate via deep link, we just select it. 
                // The effect above will handle loading comments once modal opens
                handleSeleccionarForo(found);
            }
        }
    }, [initialItem, foros, editingId]);

    const foroSeleccionado = useMemo(
        () => foros.find((f) => f.id === selectedId) || null,
        [foros, selectedId]
    );

    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleAgregarComentario = async (e) => {
        if (e) e.preventDefault();
        if (!foroSeleccionado) return;

        // Validar que el foro está abierto
        if (foroSeleccionado.estado === "cerrado") {
            setAlertDialog({
                open: true,
                title: "Foro cerrado",
                message: "No se pueden agregar comentarios a un foro cerrado. Por favor, reabre el foro primero.",
                variant: "warning"
            });
            return;
        }

        if (!nuevoComentario.trim()) {
            setErroresComentario("El comentario no puede estar vacío.");
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        const idUsuario = getSessionUserId();

        const payload = {
            IdRespuesta: 0,
            IdForo: foroSeleccionado.id,
            Mensaje: nuevoComentario.trim(),
            IdUsuario: Number(idUsuario),
            Autor: nombreUsuarioActual,
            FechaCreacion: toISODateTime(new Date())
        };
        console.log("Publicando comentario en administración:", payload);

        try {
            await axios.post(ENDPOINTS.forosRespuestas, payload);

            // Recargar respuestas usando la función centralizada
            await cargarRespuestas(foroSeleccionado.id);

            setNuevoComentario("");
            setErroresComentario("");
        } catch (error) {
            console.error("Error al publicar respuesta:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleEstadoForo = async (id) => {
        const foro = foros.find(f => f.id === id);
        if (!foro) return;

        const nuevoEstadoNombre = foro.estado === "abierto" ? "cerrado" : "abierto";
        const nuevoEstado = estadosIntranet.find(e => {
            const n = (e.nombre || e.Nombre || "").toLowerCase();
            return n === nuevoEstadoNombre;
        });

        const nuevoEstadoId = nuevoEstado?.idEstado || nuevoEstado?.IdEstado;

        if (!nuevoEstadoId) {
            console.error("No se pudo encontrar el estado:", nuevoEstadoNombre);
            return;
        }

        try {
            const idUsuario = getSessionUserId();
            const payload = {
                idForo: id,
                titulo: foro.titulo,
                descripcionInicial: foro.descripcionInicial,
                idAreaNegocio: foro.idAreaNegocio,
                idCategoria: foro.idCategoria,
                idEstado: nuevoEstadoId,
                destacado: foro.destacado,
                imagenUrl: foro.imagen || "",
                idUsuarioCreador: Number(idUsuario),
                fechaCreacion: toISODateTime(new Date()),
                fechaModificacion: toISODateTime(new Date()),
                activo: true
            };
            console.log("Payload al cambiar estado del foro:", payload);

            await axios.put(ENDPOINTS.foros, payload);

            // Actualizar estado local
            setForos((prev) =>
                prev.map((f) =>
                    f.id === id
                        ? {
                            ...f,
                            estado: nuevoEstadoNombre.toLowerCase(),
                            idEstado: nuevoEstadoId,
                        }
                        : f
                )
            );
        } catch (error) {
            console.error("Error al cambiar estado:", error);
        }
    };

    const executeDelete = async () => {
        const foroToDelete = deleteDialog.item;
        if (!foroToDelete) return;

        try {
            // Eliminar imagen física si existe
            if (foroToDelete.imagen) {
                try {
                    await axios.delete(UPLOAD_ENDPOINT, {
                        params: { filePath: foroToDelete.imagen }
                    });
                } catch (imgErr) {
                    console.warn("No se pudo eliminar la imagen física del foro:", imgErr);
                }
            }

            await axios.delete(`${ENDPOINTS.foros}/${foroToDelete.id}`);
            await recargarForos();
            if (selectedId === foroToDelete.id) {
                setSelectedId(null);
            }
            setAlertDialog({ open: true, title: "Éxito", message: "Foro eliminado correctamente.", variant: "success" });
            setDeleteDialog({ open: false, item: null });
        } catch (error) {
            console.error("Error al eliminar:", error);
        }
    };

    const handleEliminarForo = (foro) => {
        setDeleteDialog({ open: true, item: foro });
        setShowModal(false); // Cerrar el modal de detalle al abrir el de confirmación
    };

    const forosFiltrados = useMemo(() => {
        return foros.filter((f) => {
            if (
                filtros.texto &&
                !(
                    (f.titulo || "").toLowerCase().includes((filtros.texto || "").toLowerCase()) ||
                    (f.descripcionInicial || "")
                        .toLowerCase()
                        .includes((filtros.texto || "").toLowerCase())
                )
            ) {
                return false;
            }
            if (filtros.area !== "Todas" && f.area !== filtros.area) {
                return false;
            }
            if (filtros.categoria !== "Todas" && f.categoria !== filtros.categoria) {
                return false;
            }
            if (filtros.estado !== "todos" && f.estado !== filtros.estado) {
                return false;
            }
            return true;
        });
    }, [foros, filtros]);

    return (
        <div className="intra-news-page">
            {/* HEADER */}
            <header className="intra-news-header">
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                    {onNavigate && (
                        <button
                            type="button"
                            className="intra-button-ghost btn-cancel"
                            onClick={() => onNavigate("dashboard")}
                            style={{
                                marginBottom: '0.75rem'
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-news-title">Foros temáticos</h1>

                </div>
            </header>

            {/* TOOLBAR/FILTROS + NUEVO TEMA */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTextoForos">Buscar</label>
                        <input
                            id="filtroTextoForos"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Título o descripción..."
                        />
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroAreaForos">Área</label>
                        <select
                            id="filtroAreaForos"
                            name="area"
                            className="intra-select"
                            value={filtros.area}
                            onChange={handleFiltrosChange}
                        >
                            <option key="all-areas" value="Todas">Todas</option>
                            {areasNegocio.map((area, idx) => (
                                <option key={area.idAreaNegocio || area.IdAreaNegocio || `area-${idx}`} value={area.nombre || area.Nombre}>{area.nombre || area.Nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '180px' }}>
                        <label className="intra-label" htmlFor="filtroCategoriaForos">Categoría</label>
                        <select
                            id="filtroCategoriaForos"
                            name="categoria"
                            className="intra-select"
                            value={filtros.categoria}
                            onChange={handleFiltrosChange}
                        >
                            <option key="all-cats" value="Todas">Todas</option>
                            {categorias.map((cat, idx) => (
                                <option key={cat.idCategoria || cat.IdCategoria || `cat-${idx}`} value={cat.nombre || cat.Nombre}>{cat.nombre || cat.Nombre}</option>
                            ))}
                        </select>
                    </div>

                    <div className="intra-filter-group" style={{ width: '150px' }}>
                        <label className="intra-label" htmlFor="filtroEstadoForos">Estado</label>
                        <select
                            id="filtroEstadoForos"
                            name="estado"
                            className="intra-select"
                            value={filtros.estado}
                            onChange={handleFiltrosChange}
                        >
                            {ESTADOS.map((e, idx) => (
                                <option key={e.value || `estado-${idx}`} value={e.value}>{e.label}</option>
                            ))}
                        </select>
                    </div>

                    <button
                        type="button"
                        className="btn-action"
                        onClick={handleNuevoTema}
                        style={{ alignSelf: 'flex-end', whiteSpace: 'nowrap' }}
                    >
                        + Nuevo tema
                    </button>
                </div>
            </section>

            {/* LAYOUT PRINCIPAL - LISTA GRID */}
            <section className="intra-news-layout" style={{ display: 'block' }}>
                <div className="intra-news-list-panel" style={{ width: '100%' }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Temas activos</h2>
                        <p className="intra-news-count">
                            {forosFiltrados.length} tema{forosFiltrados.length === 1 ? "" : "s"} encontrados
                        </p>
                    </div>

                    {forosFiltrados.length === 0 ? (
                        <p className="intra-avisos-empty">
                            No se encontraron foros con los filtros seleccionados.
                        </p>
                    ) : (
                        <div className="intra-avisos-table-wrapper">
                            <table className="intra-avisos-table" style={{ borderCollapse: 'separate', borderSpacing: '0 10px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '80px', textAlign: 'center' }}>FAVORITO</th>
                                        <th>IMAGEN</th>
                                        <th>TÍTULO</th>
                                        <th>Área de Negocio</th>
                                        <th>Categoría</th>
                                        <th>FECHA</th>
                                        <th>ESTADO</th>
                                        <th>EDITAR</th>
                                        <th>ELIMINAR</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {forosFiltrados.map((foro, idx) => {
                                        const isDest = foro.destacado;
                                        const borderColor = '#fbbf24';
                                        const borderStyle = isDest ? `2px solid ${borderColor}` : '2px solid transparent';
                                        const bgColor = isDest ? '#fffbeb' : 'transparent';

                                        return (
                                            <tr
                                                key={foro.id || `foro-${idx}`}
                                                style={{
                                                    backgroundColor: bgColor,
                                                    // We do not put borders on TR here because of border-collapse: separate
                                                }}
                                            >
                                                <td style={{ textAlign: 'center', borderTop: borderStyle, borderBottom: borderStyle, borderLeft: borderStyle, borderTopLeftRadius: isDest ? '12px' : '0', borderBottomLeftRadius: isDest ? '12px' : '0' }}>
                                                    <span
                                                        onClick={(e) => { e.stopPropagation(); handleToggleDestacadoForo(foro); }}
                                                        style={{
                                                            cursor: 'pointer',
                                                            color: foro.destacado ? '#fbbf24' : '#e2e8f0',
                                                            fontSize: '2rem',
                                                            transition: 'color 0.2s ease'
                                                        }}
                                                        title={foro.destacado ? "Quitar favorito" : "Marcar como favorito"}
                                                    >
                                                        ★
                                                    </span>
                                                </td>
                                                <td style={{
                                                    width: '80px',
                                                    padding: '0.5rem',
                                                    borderTop: borderStyle,
                                                    borderBottom: borderStyle,
                                                }}>
                                                    {foro.imagen ? (
                                                        <img
                                                            src={foro.imagen.startsWith('http') || foro.imagen.startsWith('data:')
                                                                ? foro.imagen
                                                                : `${SERVER_URL}${foro.imagen.startsWith('/') ? foro.imagen : '/' + foro.imagen}`
                                                            }
                                                            alt={foro.titulo}
                                                            style={{
                                                                width: '60px',
                                                                height: '60px',
                                                                objectFit: 'cover',
                                                                borderRadius: '6px',
                                                                border: '1px solid #e2e8f0'
                                                            }}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.parentElement.innerHTML = '??';
                                                            }}
                                                        />
                                                    ) : (
                                                        <div style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            backgroundColor: '#f1f5f9',
                                                            borderRadius: '6px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            color: '#cbd5e1',
                                                            fontSize: '1.5rem'
                                                        }}>
                                                            ??
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{foro.titulo}</div>
                                                    <div style={{ minHeight: '1.2rem', marginTop: '0.2rem' }}>
                                                        <span style={{ fontSize: '0.7rem', backgroundColor: '#fbbf24', padding: '0.1rem 0.4rem', borderRadius: '4px', display: 'inline-block', visibility: foro.destacado ? 'visible' : 'hidden', opacity: foro.destacado ? 1 : 0, transition: 'opacity 0.2s' }}>Destacado</span>
                                                    </div>
                                                </td>
                                                <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                    <span className="intra-avisos-meta">
                                                        {foro.area}
                                                    </span>
                                                </td>
                                                <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                    <span className="intra-avisos-meta">
                                                        {foro.categoria}
                                                    </span>
                                                </td>
                                                <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>{foro.fechaCreacion.split('T')[0]}</td>
                                                <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                    <div
                                                        onClick={() => handleToggleEstadoForo(foro.id)}
                                                        style={{
                                                            width: '40px',
                                                            height: '24px',
                                                            backgroundColor: foro.estado === 'abierto' ? '#10b981' : '#cbd5e1',
                                                            borderRadius: '12px',
                                                            position: 'relative',
                                                            cursor: 'pointer',
                                                            transition: 'background-color 0.2s'
                                                        }}
                                                        title={foro.estado === 'abierto' ? "Cerrar foro" : "Abrir foro"}
                                                    >
                                                        <div style={{
                                                            width: '20px',
                                                            height: '20px',
                                                            backgroundColor: 'white',
                                                            borderRadius: '50%',
                                                            position: 'absolute',
                                                            top: '2px',
                                                            left: foro.estado === 'abierto' ? '18px' : '2px',
                                                            transition: 'left 0.2s',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                        }} />
                                                    </div>
                                                </td>
                                                <td style={{ borderTop: borderStyle, borderBottom: borderStyle }}>
                                                    {(foro.esOrigen || isSessionSuperAdmin()) && (
                                                        <button
                                                            type="button"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                            onClick={() => handleSeleccionarForo(foro)}
                                                            title="Editar foro"
                                                        >
                                                            <img src={sigmaEdit} alt="Editar" style={{ width: '40px', height: 'auto' }} />
                                                        </button>
                                                    )}
                                                </td>
                                                <td style={{
                                                    borderTop: borderStyle,
                                                    borderBottom: borderStyle,
                                                    borderRight: borderStyle,
                                                    borderTopRightRadius: isDest ? '12px' : '0',
                                                    borderBottomRightRadius: isDest ? '12px' : '0'
                                                }}>
                                                    {(foro.esOrigen || isSessionSuperAdmin()) && (
                                                        <button
                                                            type="button"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                            onClick={() => handleEliminarForo(foro)}
                                                            title="Eliminar foro"
                                                        >
                                                            <img src={sigmaDelete} alt="Eliminar" style={{ width: '40px', height: 'auto' }} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>

            {/* MODAL */}
            {showModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000,
                    padding: '2rem'
                }}>
                    <div className="intra-card" style={{
                        width: '95%',
                        maxWidth: '1000px',
                        height: '85vh',
                        maxHeight: '90vh',
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                        overflow: 'hidden'
                    }}>
                        {/* Modal Header */}
                        <div style={{
                            padding: '1.25rem 2rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: 'white',
                            borderBottom: '1px solid #f1f5f9'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        setNuevoForo(prev => ({ ...prev, destacado: !prev.destacado }));
                                        if (modalMode === 'edit' && editingId) {
                                            const foroActual = foros.find(f => f.id === editingId);
                                            if (foroActual) {
                                                handleToggleDestacadoForo(foroActual);
                                            }
                                        }
                                    }}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '1.8rem',
                                        cursor: 'pointer',
                                        padding: 0,
                                        lineHeight: 1,
                                        transition: 'all 0.2s',
                                        color: nuevoForo.destacado ? '#fbbf24' : '#e2e8f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                    title={nuevoForo.destacado ? "Quitar destacado" : "Marcar como destacado"}
                                >
                                    ★
                                </button>
                                <h3 style={{
                                    margin: 0,
                                    fontSize: '1.25rem',
                                    color: 'var(--color-primary)',
                                    fontWeight: 800,
                                    letterSpacing: '-0.02em'
                                }}>
                                    {modalMode === 'create' ? "Nuevo Tema" : "Gestionar Foro"}
                                </h3>
                            </div>
                            <button
                                onClick={() => setShowModal(false)}
                                style={{
                                    background: '#f1f5f9',
                                    border: 'none',
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '1.2rem',
                                    cursor: 'pointer',
                                    color: '#64748b',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e2e8f0'}
                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'}
                            >
                                ×
                            </button>
                        </div>

                        {/* Navigation Tabs (Only in Edit mode) */}
                        {modalMode === 'edit' && (
                            <div style={{
                                display: 'flex',
                                borderBottom: '1px solid #e2e8f0',
                                backgroundColor: 'white',
                            }}>
                                <button
                                    onClick={() => setModalTab('info')}
                                    style={{
                                        flex: 1,
                                        padding: '1.25rem 1rem',
                                        border: 'none',
                                        borderBottom: `3px solid ${modalTab === 'info' ? 'var(--color-primary)' : 'transparent'}`,
                                        background: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        color: modalTab === 'info' ? 'var(--color-primary)' : '#64748b',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    Edición / Info
                                </button>
                                <button
                                    onClick={() => setModalTab('comments')}
                                    style={{
                                        flex: 1,
                                        padding: '1.25rem 1rem',
                                        border: 'none',
                                        borderBottom: `3px solid ${modalTab === 'comments' ? 'var(--color-primary)' : 'transparent'}`,
                                        background: 'none',
                                        fontWeight: 700,
                                        fontSize: '0.9rem',
                                        color: modalTab === 'comments' ? 'var(--color-primary)' : '#64748b',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em'
                                    }}
                                >
                                    Comentarios ({foroSeleccionado?.comentarios?.length || 0})
                                </button>
                            </div>
                        )}

                        {/* Modal Content */}
                        <div style={{ display: 'flex', flex: 1, flexDirection: 'column', overflow: 'hidden' }}>
                            {/* Information / Edit Tab */}
                            {(modalMode === 'create' || modalTab === 'info') && (
                                <div style={{ flex: 1, overflowY: 'auto', padding: '2.5rem', backgroundColor: 'white' }}>
                                    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
                                        <form className="intra-avisos-form" onSubmit={handleCrearForo}>
                                            <div className="intra-form-group">
                                                <label className="intra-label" htmlFor="tituloForo">Título del tema</label>
                                                <input
                                                    id="tituloForo"
                                                    name="titulo"
                                                    type="text"
                                                    className={`intra-input ${erroresForo.titulo ? "intra-input-error" : ""}`}
                                                    value={nuevoForo.titulo}
                                                    onChange={handleNuevoForoChange}
                                                    placeholder="Ej. Sugerencias para mejorar la seguridad en planta"
                                                />
                                                {erroresForo.titulo && <p className="intra-error-text">{erroresForo.titulo}</p>}
                                            </div>

                                            <div className="intra-form-group">
                                                <label className="intra-label" htmlFor="descripcionInicialForo">Descripción inicial</label>
                                                <textarea
                                                    id="descripcionInicialForo"
                                                    name="descripcionInicial"
                                                    className={`intra-textarea ${erroresForo.descripcionInicial ? "intra-input-error" : ""}`}
                                                    rows={6}
                                                    value={nuevoForo.descripcionInicial}
                                                    onChange={handleNuevoForoChange}
                                                    placeholder="Explica el objetivo del tema..."
                                                />
                                                {erroresForo.descripcionInicial && <p className="intra-error-text">{erroresForo.descripcionInicial}</p>}
                                            </div>

                                            <div className="intra-form-row">
                                                <div className="intra-form-group" style={{ flex: 1 }}>
                                                    <label className="intra-label">Área</label>
                                                    <select
                                                        name="area"
                                                        className="intra-select"
                                                        value={nuevoForo.area || ""}
                                                        onChange={handleNuevoForoChange}
                                                    >
                                                        {areasNegocio.map((a, idx) => (
                                                            <option key={a.idAreaNegocio || a.IdAreaNegocio || `modal-area-${idx}`} value={a.idAreaNegocio || a.IdAreaNegocio}>{a.nombre || a.Nombre}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="intra-form-group" style={{ flex: 1 }}>
                                                    <label className="intra-label">Categoría</label>
                                                    <select
                                                        name="categoria"
                                                        className="intra-select"
                                                        value={nuevoForo.categoria || ""}
                                                        onChange={handleNuevoForoChange}
                                                    >
                                                        {categorias.map((c, idx) => (
                                                            <option key={c.idCategoria || c.IdCategoria || `modal-cat-${idx}`} value={c.idCategoria || c.IdCategoria}>{c.nombre || c.Nombre}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="intra-form-group" style={{ flex: 1 }}>
                                                    <label className="intra-label">Estado</label>
                                                    <select
                                                        name="estado"
                                                        className="intra-select"
                                                        value={nuevoForo.estado || ""}
                                                        onChange={handleNuevoForoChange}
                                                    >
                                                        {estadosIntranet.map((e, idx) => (
                                                            <option key={e.idEstado || e.IdEstado || `modal-estado-${idx}`} value={e.idEstado || e.IdEstado}>{e.nombre || e.Nombre}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="intra-form-group">
                                                <label className="intra-label" htmlFor="imagenForo">Imagen adjunta (opcional)</label>
                                                <div className={`file-uploader${imagePreview ? ' file-uploader--filled' : ''}`}>
                                                    {imagePreview && (
                                                        <div className="file-uploader__badge">
                                                            <span>✓</span>
                                                            <span>Archivo cargado</span>
                                                            <span
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    setImagePreview(null);
                                                                    setSelectedFile(null);
                                                                    setNuevoForo(prev => ({ ...prev, imagen: null }));
                                                                }}
                                                                style={{ marginLeft: '4px', cursor: 'pointer', fontWeight: 'bold' }}
                                                            >
                                                                ×
                                                            </span>
                                                        </div>
                                                    )}
                                                    {imagePreview && (
                                                        <div className="file-uploader__preview">
                                                            <img src={imagePreview} alt="Vista previa" style={{ maxHeight: '150px', maxWidth: '100%', borderRadius: '4px' }} />
                                                        </div>
                                                    )}
                                                    <label className="file-uploader__label">
                                                        <input
                                                            type="file"
                                                            id="imagenForo"
                                                            accept="image/*"
                                                            onClick={(e) => { e.target.value = ''; }}
                                                            onChange={handleFileChange}
                                                        />
                                                        <span className="file-uploader__icon">{imagePreview ? '✓' : '📁'}</span>
                                                        <span className="file-uploader__text">{imagePreview ? 'Cambiar imagen' : 'Haz clic para subir imagen'}</span>
                                                        <span className="file-uploader__hint">JPG, PNG, WEBP (Máx. 5MB)</span>
                                                    </label>
                                                </div>
                                            </div>
                                        </form>

                                        {/* Footer Actions */}
                                        <div style={{ marginTop: '2.5rem', display: 'flex', justifyContent: 'space-between', gap: '1rem', borderTop: '1px solid #f1f5f9', paddingTop: '1.5rem' }}>
                                            <div>
                                                {modalMode === 'edit' && (
                                                    <button
                                                        type="button"
                                                        className="intra-button-ghost"
                                                        onClick={() => handleEliminarForo(foroSeleccionado)}
                                                        style={{ color: '#ef4444', borderColor: '#fee2e2' }}
                                                    >
                                                        Eliminar Foro
                                                    </button>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', gap: '1rem' }}>
                                                <button
                                                    type="button"
                                                    className="intra-button-ghost"
                                                    onClick={() => setShowModal(false)}
                                                >
                                                    Cancelar
                                                </button>
                                                {(modalMode === 'create' || foroSeleccionado?.esOrigen || isSessionSuperAdmin()) && (
                                                    <button
                                                        type="button"
                                                        className="btn-action"
                                                        onClick={handleCrearForo}
                                                        disabled={isUploading}
                                                        style={{ opacity: isUploading ? 0.7 : 1, cursor: isUploading ? 'not-allowed' : 'pointer' }}
                                                    >
                                                        {isUploading ? 'Guardando...' : (modalMode === 'create' ? 'Crear Tema' : 'Guardar Cambios')}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Comments Tab */}
                            {modalMode === 'edit' && modalTab === 'comments' && (
                                <div style={{
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: '#f8fafc',
                                    overflow: 'hidden'
                                }}>
                                    {/* Conversation Header */}
                                    <div style={{ padding: '1rem 2rem', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 700, color: '#1e293b' }}>Conversación del hilo</span>
                                        <span style={{ fontSize: '0.85rem', color: '#64748b', backgroundColor: '#f1f5f9', padding: '2px 10px', borderRadius: '12px' }}>
                                            {foroSeleccionado?.comentarios?.length || 0} mensajes
                                        </span>
                                    </div>

                                    {/* Comments List */}
                                    <div style={{ flex: 1, overflowY: 'auto', padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                        {foroSeleccionado?.comentarios && foroSeleccionado.comentarios.length > 0 ? (
                                            foroSeleccionado.comentarios.map((comentario, index) => {
                                                const initials = comentario.autor
                                                    ? comentario.autor.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                                    : "U";
                                                const bgColors = ['#FCA5A5', '#FCD34D', '#34D399', '#60A5FA', '#818CF8', '#A78BFA', '#F472B6'];
                                                const avatarColor = bgColors[index % bgColors.length];

                                                const isOwner = Number(comentario.idUsuario) === Number(foroSeleccionado.creadoPor);
                                                const userFoto = fotosMap[String(comentario.idUsuario)];

                                                return (
                                                    <div key={comentario.id || `comment-${index}`} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                                        <div style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            minWidth: '40px',
                                                            borderRadius: '50%',
                                                            backgroundColor: (userFoto && userFoto !== "") ? 'transparent' : (isOwner ? '#0f172a' : avatarColor),
                                                            color: 'white',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '0.9rem',
                                                            fontWeight: 'bold',
                                                            border: isOwner && (!userFoto || userFoto === "") ? '2px solid #e2e8f0' : 'none',
                                                            overflow: 'hidden'
                                                        }}>
                                                            {(userFoto && userFoto !== "") ? (
                                                                <img src={getImageUrl(userFoto)} alt={comentario.autor} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'flex'; }} />
                                                            ) : null}
                                                            <div style={{ display: (userFoto && userFoto !== "") ? 'none' : 'flex', width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                                                                {isOwner ? '👑' : initials}
                                                            </div>
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
                                                                <span style={{ fontWeight: 700, color: '#1e293b' }}>{comentario.autor}</span>
                                                                <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                                                                    {comentario.fecha ? new Date(comentario.fecha).toLocaleDateString('es-MX', {
                                                                        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                                    }) : ''}
                                                                </span>
                                                            </div>
                                                            <div style={{
                                                                backgroundColor: 'white',
                                                                padding: '1rem',
                                                                borderRadius: '0 12px 12px 12px',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                                border: '1px solid #f1f5f9',
                                                                color: '#334155',
                                                                lineHeight: '1.5',
                                                                fontSize: '0.95rem'
                                                            }}>
                                                                {comentario.mensaje}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#94a3b8', gap: '1rem', textAlign: 'center' }}>
                                                <span style={{ fontSize: '3rem' }}>💬</span>
                                                <div>
                                                    <p style={{ margin: 0, fontWeight: 700 }}>No hay respuestas todavía</p>
                                                    <p style={{ fontSize: '0.85rem', margin: '4px 0 0 0' }}>El hilo de conversación está vacío.</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Create Comment Form */}
                                    <div style={{ padding: '1.25rem 2rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0' }}>
                                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                                            <div style={{
                                                width: '36px',
                                                height: '36px',
                                                borderRadius: '50%',
                                                backgroundColor: fotoUsuarioActual ? 'transparent' : 'var(--color-primary)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                color: 'white'
                                            }}>
                                                {fotoUsuarioActual ? (
                                                    <img src={getImageUrl(fotoUsuarioActual)} alt={nombreUsuarioActual} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                ) : (
                                                    <span style={{ fontSize: '0.8rem', fontWeight: 700 }}>YO</span>
                                                )}
                                            </div>
                                            <div style={{ flex: 1, position: 'relative' }}>
                                                <input
                                                    type="text"
                                                    value={nuevoComentario}
                                                    onChange={(e) => setNuevoComentario(e.target.value)}
                                                    placeholder="Escribe un comentario administrativo..."
                                                    style={{
                                                        width: '100%',
                                                        padding: '0.75rem 3rem 0.75rem 1.25rem',
                                                        borderRadius: '999px',
                                                        border: '1px solid #e2e8f0',
                                                        backgroundColor: '#f8fafc',
                                                        outline: 'none',
                                                        fontSize: '0.95rem'
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleAgregarComentario(e);
                                                    }}
                                                />
                                                <button
                                                    onClick={handleAgregarComentario}
                                                    disabled={!nuevoComentario.trim() || isSubmitting}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '8px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        background: 'var(--color-primary)',
                                                        color: 'white',
                                                        border: 'none',
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '50%',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: (!nuevoComentario.trim() || isSubmitting) ? 0.5 : 1
                                                    }}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                        <line x1="22" y1="2" x2="11" y2="13"></line>
                                                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <ConfirmDeleteDialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })} onConfirm={executeDelete} roleName={deleteDialog.item?.titulo || "foro"} />
            <AlertDialog open={alertDialog.open} onClose={() => setAlertDialog({ ...alertDialog, open: false })} title={alertDialog.title} message={alertDialog.message} variant={alertDialog.variant} />
            {showShareModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050, padding: '2rem' }}>
                  <div style={{ width: '100%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                      <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Compartir Foro</h2>
                      <button type="button" onClick={() => { setShowShareModal(false); setShowModal(true); }} style={{ background: 'none', border: 'none', fontSize: '2rem', color: '#64748b', cursor: 'pointer' }}>×</button>
                    </div>
                    <div style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
                      <p style={{ marginBottom: '1.5rem', color: '#475569' }}>Seleccione las empresas con las que desea compartir este foro:</p>
                      
                      {descendantCompanies.length === 0 ? (
                         <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No hay empresas hijas disponibles.</p>
                      ) : (
                         <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                           {descendantCompanies.map(comp => {
                             const cId = comp.IdEmpresa || comp.idEmpresa;
                             const cName = comp.Nombre || comp.nombre;
                             const cParent = comp.NombrePadre || comp.nombrePadre || 'N/A';
                             return (
                             <div key={cId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                                <div>
                                   <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{cName}</div>
                                   <div style={{ fontSize: '0.85rem', color: '#64748b' }}>({cParent})</div>
                                </div>
                                <div>
                                   <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                     <input 
                                       type="checkbox" 
                                       checked={!!selectedCompanies[cId]}
                                       onChange={(e) => setSelectedCompanies(prev => ({ ...prev, [cId]: e.target.checked }))}
                                       style={{ opacity: 0, width: 0, height: 0 }}
                                     />
                                     <span style={{
                                       position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                       backgroundColor: selectedCompanies[cId] ? '#10b981' : '#cbd5e1',
                                       transition: '.4s', borderRadius: '24px'
                                     }}>
                                       <span style={{
                                         position: 'absolute', content: '""', height: '18px', width: '18px', left: '3px', bottom: '3px',
                                         backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                         transform: selectedCompanies[cId] ? 'translateX(20px)' : 'translateX(0)'
                                       }} />
                                     </span>
                                   </label>
                                </div>
                             </div>
                           )})}
                         </div>
                      )}
                    </div>
                    <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                       <button 
                         type="button" 
                         onClick={() => { setShowShareModal(false); setShowModal(true); }}
                         className="intra-button-ghost"
                       >
                         Cancelar
                       </button>
                       <button 
                         type="button" 
                         onClick={() => executePublish(selectedCompanies)}
                         className="btn-action"
                         disabled={sharingLoading}
                         style={{ opacity: sharingLoading ? 0.7 : 1 }}
                       >
                         {sharingLoading ? 'Publicando...' : 'Confirmar y Guardar'}
                       </button>
                    </div>
                  </div>
                </div>
              )}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog({ open: false, title: "", message: "", variant: "info" })}
            />
        </div>
    );
};

export default ForosTematicos;
